import { Icon } from '@iconify/react';
import { useState, useEffect, useMemo } from 'react';
import searchFill from '@iconify/icons-eva/search-fill';
import { useNavigate } from 'react-router-dom';
// material
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Input,
  Slide,
  Button,
  InputAdornment,
  ClickAwayListener,
  Grid,
  Typography,
  TextField,
  Stack,
  Divider,
  IconButton,
  InputBase,
  useMediaQuery,
  Avatar
} from '@mui/material';
// components
import { MIconButton } from '../../components/@material-extend';

// routes
import { PATH_STOCK } from '../../routes/paths';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';

import throttle from 'lodash/throttle';
import { useDispatch, useSelector, RootState } from 'redux/store';
import { searchStocks } from 'redux/slices/stockSearch';
import { getOptionByTag } from 'redux/slices/stock';
import { ButtonTracking, stockBtnTabEventTracking, event_types } from 'components/amplitute';
import {
  CongressPoliticianOption,
  searchCongressPoliticians
} from 'pages/insiderTrading/congressApi';

import SearchIcon from '@mui/icons-material/Search';

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

type StockSearchOption = {
  resultType: 'stock';
  ticker?: string;
  data?: {
    name?: string;
    primary_exchange?: string;
  };
};

type CongressSearchOption = CongressPoliticianOption & {
  resultType: 'congress';
};

type SearchOption = StockSearchOption | CongressSearchOption;

const SearchbarStyle = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  backgroundColor: `${alpha(theme.palette.background.default, 0.72)}`,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

// ----------------------------------------------------------------------

export default function Searchbar() {
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [value, setValue] = useState<any>([]);
  const [inputValue, setInputValue] = useState('');
  const [congressSearchList, setCongressSearchList] = useState<CongressSearchOption[]>([]);

  const dispatch = useDispatch();
  const { stockSearchList } = useSelector((state: RootState) => state.stockSearch);
  const isMobile = useMediaQuery('(max-width:600px)');

  const options = useMemo<SearchOption[]>(() => {
    if (!inputValue.trim()) return [];

    const stockOptions = (stockSearchList || []).map((item: any) => ({
      ...item,
      resultType: 'stock'
    }));

    return [...stockOptions, ...congressSearchList];
  }, [stockSearchList, congressSearchList, inputValue]);

  const fetch = useMemo(
    () =>
      throttle(async (request) => {
        dispatch(searchStocks(request));
        dispatch(getOptionByTag(request));
        try {
          const people = await searchCongressPoliticians(request);
          setCongressSearchList(
            people.map((person) => ({
              ...person,
              resultType: 'congress'
            }))
          );
        } catch {
          setCongressSearchList([]);
        }
      }, 200),
    []
  );

  useEffect(() => {
    if (inputValue === '') {
      setCongressSearchList([]);
      return undefined;
    }

    fetch(inputValue);
  }, [value, inputValue, fetch]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSearchStock = (value: any) => {
    stockBtnTabEventTracking(
      'Dashboard',
      value,
      event_types.btnClick,
      `Search from Header`,
      'Searchbar',
      window.location.href
    );
    navigate(PATH_STOCK.detail + '/' + value);
  };

  const getCongressName = (option: CongressSearchOption) => {
    return (
      option.fullName ||
      `${option.firstName || ''} ${option.lastName || ''}`.trim() ||
      option.uniqueInsiderId ||
      ''
    );
  };

  const getCongressInitials = (option: CongressSearchOption) => {
    const name = getCongressName(option);
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  };

  const handleSearchSelection = (option: SearchOption) => {
    if (!option) return;

    if (option.resultType === 'congress') {
      stockBtnTabEventTracking(
        'Dashboard',
        getCongressName(option),
        event_types.btnClick,
        `Search congress person from Header`,
        'Searchbar',
        window.location.href
      );
      navigate(
        `/congress_trader_profile?insiderId=${encodeURIComponent(option.uniqueInsiderId || '')}`
      );
      return;
    }

    handleSearchStock(option.ticker);
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      getOptionLabel={(option: any) => {
        if (typeof option === 'string') return option;
        if (option?.resultType === 'congress') return getCongressName(option);
        return option?.ticker || '';
      }}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      disableClearable
      includeInputInList
      autoHighlight
      noOptionsText="Sorry, no matches have been found ..."
      value={value}
      sx={!isMobile ? { maxWidth: 380, ml: 2 } : { maxWidth: 340 }}
      onChange={(event, newValue: any) => {
        setValue(newValue);
        handleSearchSelection(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <Box
          ref={params.InputProps.ref}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: isMobile ? 340 : 380,
            border: '2px solid #1976d2',
            borderRadius: '25px',
            paddingLeft: 2,
            backgroundColor: '#fff',
            '& .MuiAutocomplete-root': {
              maxWidth: isMobile ? 340 : 380
            }
          }}
        >
          {/* @ts-ignore */}
          <InputBase
            {...params.inputProps}
            placeholder="Search stocks or congress people"
            sx={{
              flex: 1,
              fontSize: 16
            }}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'search stocks or congress people'
            }}
          />
          <IconButton
            type="submit"
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              padding: '8px',
              borderRadius: '0 25px 25px 0',
              '&:hover': {
                backgroundColor: '#115293'
              }
            }}
            aria-label="search"
          >
            <SearchIcon />
          </IconButton>
        </Box>
      )}
      renderOption={(props, option: any) => (
        <li {...props}>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ borderBottom: '1px solid #cccccc', width: '100%' }}
          >
            {option?.resultType === 'congress' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexGrow: 1 }}>
                  <Avatar src={option.imageUrl} sx={{ width: 32, height: 32, fontSize: 12 }}>
                    {getCongressInitials(option)}
                  </Avatar>
                  <Box>
                    <Typography color="primary">{getCongressName(option)}</Typography>
                    <Typography variant="caption">Congress trader</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography color="text.secondary">Politician</Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography color="primary">{option?.ticker}</Typography>
                  <Typography variant="caption">{option?.data?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography color="text.secondary">{option?.data?.primary_exchange}</Typography>
                </Box>
              </>
            )}
          </Box>
        </li>
      )}
    />
  );
}
